import { useState } from "react";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "@norkron/web-menu";
import { MoreHorizontal, PenLine, Repeat2, Trash2 } from "../compose/Icon";

/**
 * The per-row lifecycle menu on the Articles list — rename / take parts /
 * delete.
 *
 * ── WHY THIS IS A PORTALLED FLOATING MENU, NOT AN ABSOLUTE PANEL ───────────
 * The list container `.doclist` is `overflow: hidden` (it clips its rows'
 * rounded corners), and above it `.shell__main` is `overflow: auto` and
 * `.shell` is `overflow: hidden`. An absolutely-positioned panel is clipped by
 * ANY such ancestor, and `z-index` CANNOT escape a clipping ancestor — so the
 * LAST row's menu was cut off: measured 108px of a 138px panel clipped away,
 * leaving a sliced "Rename…" and no Delete at all.
 *
 * `overflow: visible` on `.doclist` is NOT the fix — it would break the row
 * clipping the list is styled for. The fix is to leave the clipping subtree
 * entirely: `<MenuContent>` portals to `document.body` and is positioned
 * `fixed` against the trigger by `@norkron/web-floating`, which flips the
 * surface above the trigger when there is no room below and shifts it to stay
 * inside the viewport. RULE ZERO: we compose the ecosystem's menu primitive
 * rather than re-rolling portalling, collision math, or menu keyboard support.
 *
 * ── IT LIVES INSIDE A <Link>, WHICH DICTATES THE EVENT HANDLING ────────────
 * `<ArticleUnitRow>` wraps the WHOLE card in a single `<Link>` to the
 * workbench, and `actions` renders inside it. Portalling does NOT change that:
 * React replays synthetic events through the REACT tree, not the DOM tree, so
 * a click inside the portal still reaches react-router's `onClick` on the
 * `<Link>`. Every control here therefore still calls `preventDefault()` +
 * `stopPropagation()` — without both, using the menu navigates away from the
 * list the menu acts on. (`preventDefault` alone stops the anchor's default
 * but still lets React Router's handler run; `stopPropagation` alone leaves
 * the default.) `stopPropagation` only blocks ancestors, so the menu's own
 * open/close still fires — `MenuTrigger`/`MenuItem` `asChild` compose our
 * handler BEFORE their own on the SAME element.
 *
 * ── CAPABILITY-GATED, HIDDEN NOT DISABLED ──────────────────────────────────
 * The server gates rename + parts on `edit_doc` and delete on `admin_workspace`
 * (`api/article_admin_service.zig`). An item the caller cannot use is not
 * rendered at all — a live-looking control whose every write the server rejects
 * is the decoration this codebase already pins shut elsewhere. The SERVER stays
 * authoritative; this only decides what to draw.
 *
 * Dropdown behaviour mirrors `<DownloadMenu>`: outside-click + Escape close it
 * (via `@norkron/web-dismiss`, capture-phase and topmost-layer-only, so Escape
 * does not also close anything behind the menu), focus returns to the trigger,
 * `aria-haspopup="menu"` on the trigger, `role="menu"` / `role="menuitem"` on
 * the panel and its items.
 */
export function ArticleRowMenu({
  unitId,
  canEdit,
  canAdmin,
  onRename,
  onMerge,
  onDelete,
}: {
  unitId: string;
  /** `edit_doc` — rename + take-parts. */
  canEdit: boolean;
  /** `admin_workspace` — delete. */
  canAdmin: boolean;
  onRename: () => void;
  onMerge: () => void;
  onDelete: () => void;
}) {
  // Controlled: an uncontrolled <Menu> fires `onOpenChange` twice per
  // transition (it drives both useDisclosure and useControlledState).
  const [open, setOpen] = useState(false);

  // Nothing this caller may do ⇒ no trigger at all.
  if (!canEdit && !canAdmin) return null;

  /** Swallow the row's navigation. See the event-handling note above. */
  const swallow = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Menu
      // Gives the portalled surface a deterministic id — `MenuContent` takes no
      // `data-testid` (its props are zod-stripped), so
      // `#article-menu-<id>-content` is the stable handle for tests and E2E.
      id={`article-menu-${unitId}`}
      open={open}
      onOpenChange={setOpen}
      placement="bottom-end"
    >
      <MenuTrigger asChild ariaLabel="Project actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          data-testid={`article-menu-trigger-${unitId}`}
          onClick={swallow}
        >
          <MoreHorizontal size={16} strokeWidth={1.5} />
        </button>
      </MenuTrigger>

      <MenuContent className="ev-menu__surface" ariaLabel="Project actions">
        {canEdit ? (
          <MenuItem asChild label="Rename…" onSelect={onRename}>
            <button
              type="button"
              className="ev-menu__item"
              data-testid={`article-menu-rename-${unitId}`}
              onClick={swallow}
            >
              <PenLine size={14} strokeWidth={1.5} /> Rename…
            </button>
          </MenuItem>
        ) : null}
        {canEdit ? (
          <MenuItem
            asChild
            label="Take parts from another project…"
            onSelect={onMerge}
          >
            <button
              type="button"
              className="ev-menu__item"
              data-testid={`article-menu-merge-${unitId}`}
              onClick={swallow}
            >
              <Repeat2 size={14} strokeWidth={1.5} /> Take parts from another
              project…
            </button>
          </MenuItem>
        ) : null}
        {canAdmin ? (
          <MenuItem asChild label="Delete the project…" onSelect={onDelete}>
            <button
              type="button"
              className="ev-menu__item"
              data-testid={`article-menu-delete-${unitId}`}
              onClick={swallow}
            >
              <Trash2 size={14} strokeWidth={1.5} /> Delete the project…
            </button>
          </MenuItem>
        ) : null}
      </MenuContent>
    </Menu>
  );
}
